/**
 * Data layer for `multi-language-partner-portal`. Thin typed fetch wrappers over /api/v1/multi-language-partner-portal.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface MultiLanguagePartnerPortalRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListMultiLanguagePartnerPortalResponse {
	count: number;
	items: MultiLanguagePartnerPortalRow[];
}

export interface CreateMultiLanguagePartnerPortalBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/multi-language-partner-portal";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListMultiLanguagePartnerPortalResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListMultiLanguagePartnerPortalResponse>(res);
}

export async function create(body: CreateMultiLanguagePartnerPortalBody): Promise<MultiLanguagePartnerPortalRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<MultiLanguagePartnerPortalRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const multiLanguagePartnerPortalQueryKeys = {
	all: ["multi-language-partner-portal"] as const,
	list: () => [...multiLanguagePartnerPortalQueryKeys.all] as const,
	detail: (id: string) => [...multiLanguagePartnerPortalQueryKeys.all, id] as const,
};
